import type {
  CliAuthorizationDecision,
  CliAuthorizationFailureKind,
  CliAuthorizationStatus,
  CliDeviceAuthorization,
} from './types'

export const CLI_USER_CODE_PATTERN = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/

export type CliAuthorizationStage =
  | 'auth_loading'
  | 'awaiting_code'
  | 'signed_out'
  | 'token_pending'
  | 'inspecting'
  | 'ready'
  | 'submitting'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'consumed'
  | 'invalid'
  | 'rate_limited'
  | 'network_error'
  | 'auth_error'
  | 'unexpected_error'

export interface CliAuthorizationViewState {
  stage: CliAuthorizationStage
  code: string
  authorization: CliDeviceAuthorization | null
  decision: CliAuthorizationDecision | null
}

export type CliAuthorizationViewEvent =
  | { type: 'AUTH_LOADING' }
  | { type: 'AWAIT_CODE' }
  | { type: 'SIGNED_OUT'; code: string }
  | { type: 'TOKEN_PENDING'; code: string }
  | { type: 'INSPECTING'; code: string }
  | { type: 'INSPECTED'; code: string; authorization: CliDeviceAuthorization }
  | { type: 'INSPECT_FAILED'; code: string; failure: CliAuthorizationFailureKind }
  | { type: 'DECISION_STARTED'; decision: CliAuthorizationDecision }
  | { type: 'DECISION_FINISHED'; status: CliAuthorizationStatus }
  | { type: 'DECISION_FAILED'; failure: CliAuthorizationFailureKind }

export const initialCliAuthorizationState: CliAuthorizationViewState = {
  stage: 'auth_loading',
  code: '',
  authorization: null,
  decision: null,
}

export function formatCliUserCodeInput(value: string): string {
  const compact = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8)
  return compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact
}

export function normalizeCliUserCode(value: string | null | undefined): string | null {
  if (!value) return null
  const formatted = formatCliUserCodeInput(value)
  return CLI_USER_CODE_PATTERN.test(formatted) ? formatted : null
}

const stageFromAuthorizationStatus = (status: CliAuthorizationStatus): CliAuthorizationStage => {
  if (status === 'pending') return 'ready'
  return status
}

const stageFromFailure = (failure: CliAuthorizationFailureKind): CliAuthorizationStage => {
  if (failure === 'invalid') return 'invalid'
  if (failure === 'rate_limited') return 'rate_limited'
  if (failure === 'network_error') return 'network_error'
  if (failure === 'auth_error') return 'auth_error'
  return 'unexpected_error'
}

export function reduceCliAuthorizationState(
  state: CliAuthorizationViewState,
  event: CliAuthorizationViewEvent
): CliAuthorizationViewState {
  switch (event.type) {
    case 'AUTH_LOADING':
      return { ...initialCliAuthorizationState, stage: 'auth_loading', code: state.code }
    case 'AWAIT_CODE':
      return { ...initialCliAuthorizationState, stage: 'awaiting_code' }
    case 'SIGNED_OUT':
      return { ...initialCliAuthorizationState, stage: 'signed_out', code: event.code }
    case 'TOKEN_PENDING':
      return { ...initialCliAuthorizationState, stage: 'token_pending', code: event.code }
    case 'INSPECTING':
      return { ...initialCliAuthorizationState, stage: 'inspecting', code: event.code }
    case 'INSPECTED':
      return {
        stage: stageFromAuthorizationStatus(event.authorization.status),
        code: event.code,
        authorization: event.authorization,
        decision: null,
      }
    case 'INSPECT_FAILED':
      return {
        ...initialCliAuthorizationState,
        stage: stageFromFailure(event.failure),
        code: event.code,
      }
    case 'DECISION_STARTED':
      if (state.stage !== 'ready' || !state.authorization) return state
      return { ...state, stage: 'submitting', decision: event.decision }
    case 'DECISION_FINISHED':
      if (state.stage !== 'submitting') return state
      return { ...state, stage: stageFromAuthorizationStatus(event.status), decision: null }
    case 'DECISION_FAILED':
      if (state.stage !== 'submitting') return state
      return { ...state, stage: stageFromFailure(event.failure), decision: null }
  }
}

export function classifyCliAuthorizationError(error: unknown): CliAuthorizationFailureKind {
  if (!error || typeof error !== 'object') return 'unexpected'

  const candidate = error as {
    status?: unknown
    code?: unknown
    isNetwork?: unknown
    name?: unknown
  }

  if (candidate.isNetwork === true) return 'network_error'
  if (candidate.name === 'TypeError' && candidate.status === undefined) return 'network_error'

  const status =
    typeof candidate.status === 'number'
      ? candidate.status
      : typeof candidate.code === 'number'
        ? candidate.code
        : undefined

  if (status === 429) return 'rate_limited'
  if (status === 401 || status === 403) return 'auth_error'
  if (status === 400 || status === 404 || status === 410) return 'invalid'
  return 'unexpected'
}
